import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Link2 } from "lucide-react";
import { copyToClipboard } from "@/i18n/copyable-reference";
import { Button } from "@/components/ui/button";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { formatMoney } from "@/features/invoicing/money";
import type { IssueInvoiceResponse } from "@/features/invoicing/types";
import { COPY } from "./copy";

/**
 * The one-time pay link reveal (`POST /v1/invoices/{id}/issue`).
 *
 * The contract returns `payLink.payUrl` exactly once — at issue and at rotate —
 * and never again. So this panel holds the URL **in React state only**, for the
 * life of the reveal:
 *
 *  - it is never written to `localStorage`/`sessionStorage`, never put in the
 *    URL or a query string, and never logged;
 *  - the full URL is shown, not shortened, because the operator has one chance
 *    to copy it and a truncated link is a broken link;
 *  - the copy control reports what actually happened — a refused clipboard
 *    write says so rather than claiming success, and the link stays selectable.
 *
 * The invoice number and the payer are shown beside it: the link itself carries
 * neither (G15), and saying so is part of not over-promising.
 */

type CopyState = "idle" | "copied" | "failed";

const COPIED_RESET_MS = 2000;

export function PayLinkReveal({ invoice }: { invoice: IssueInvoiceResponse }) {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const payUrl = invoice.payLink.payUrl;

  async function handleCopy() {
    const ok = await copyToClipboard(payUrl);
    setCopyState(ok ? "copied" : "failed");
    if (resetTimer.current) clearTimeout(resetTimer.current);
    if (ok) resetTimer.current = setTimeout(() => setCopyState("idle"), COPIED_RESET_MS);
  }

  return (
    <section
      className="flex flex-col gap-4 rounded-lg border border-success-border bg-success-soft p-4 sm:p-5"
      aria-labelledby="invoice-issued-heading"
      data-testid="pay-link-reveal"
    >
      <div className="flex items-start gap-3">
        <Link2 aria-hidden className="mt-0.5 size-5 shrink-0 text-success" />
        <div>
          <h2 id="invoice-issued-heading" className="text-base font-bold text-ink">
            {COPY.issuedHeading}
          </h2>
          <p className="mt-1 max-w-[560px] text-sm leading-relaxed text-muted">{COPY.issuedDescription}</p>
        </div>
      </div>

      <div className="rounded-md border border-line bg-surface p-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted">{COPY.payLinkLabel}</div>
        {/* The whole URL, wrapping rather than clipped: it is copied once and a
            clipped link is indistinguishable from a complete one on screen. */}
        <code
          dir="ltr"
          data-testid="pay-link-url"
          className="mt-1 block break-all font-mono text-xs text-ink"
        >
          {payUrl}
        </code>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" onClick={() => void handleCopy()}>
            <Copy aria-hidden />
            {copyState === "copied" ? COPY.payLinkCopied : COPY.payLinkCopy}
          </Button>
          {copyState === "copied" && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-success" role="status" aria-live="polite">
              <Check aria-hidden className="size-3.5" />
              {COPY.payLinkCopied}
            </span>
          )}
          {copyState === "failed" && (
            <span className="text-xs font-semibold text-danger" role="status" aria-live="polite">
              {COPY.payLinkCopyFailed}
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">{COPY.payLinkNotInUrl}</p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        <Field label={COPY.issuedNumber} value={invoice.number ?? "—"} />
        <Field label={COPY.issuedAsset} value={invoice.assetSymbol} />
        <Field
          label={COPY.issuedTotal}
          value={`${formatMoney(invoice.totals.total)} ${invoice.totals.total.asset.symbol}`}
        />
        <Field label={COPY.issuedDueDate} value={invoice.dueDate ?? "—"} />
        <Field label={COPY.issuedExpiry} value={invoice.expiresAt ?? "—"} />
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted">Invoice id</dt>
          <dd className="text-sm font-semibold text-ink">
            <IdentifierValue value={invoice.invoiceId} kind="id" copy />
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap gap-3">
        <Link className="text-sm font-semibold text-primary underline" to="/stablecoins/invoices">
          {COPY.backToInvoices}
        </Link>
      </div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="break-all text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}
